import state from '../state';
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { q1O, s0 } from '../util/nE';
gsap.registerPlugin(SplitText, ScrollTrigger);

class Capabilities {
  constructor(el) {
    this.el = el;
    this.label = this.el.querySelector('.capabilities__label');
    this.title = this.el.querySelector('.capabilities__title');
    this.wysiwyg = this.el.querySelector('.capabilities__wysiwyg');
    this.listItems = this.el.querySelectorAll('.capabilities__item');
    this.itemTitles = this.el.querySelectorAll('.capabilities__item-title');
    this.arrowIcons = this.el.querySelectorAll('.capabilities__arrow-icon');
  }

  init() {
    this.buildSplitText();
    this.buildTimelineIn();

    this.isXl = false;
    const mm = gsap.matchMedia();
    mm.add(state.queries.xl, () => {
      this.isXl = true;
    });

    if (this.isXl) {
      this.buildHover();
    }
  }

  buildSplitText(el) {
    if (!el) {
      return;
    }
    const split = new SplitText(el, {
      type: 'chars, words, lines',
      linesClass: 's-line u-t overflow-hidden',
      lineThreshold: 10,
    });

    return split;
  }

  buildTimelineIn() {
    this.split = this.buildSplitText(this.title);
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.el,
      },
      defaults: {
        ease: t => {
          return q1O(t);
        },
        duration: 1,
      },
    });

    tl.set(this.el, { autoAlpha: 1 });

    if (this.label) {
      tl.fromTo(
        this.label,
        {
          yPercent: 100,
          autoAlpha: 0,
        },
        {
          yPercent: 0,
          autoAlpha: 1,
        },
        0
      );
    }

    if (this.title) {
      tl.fromTo(
        this.split.words,
        {
          yPercent: 100,
          autoAlpha: 0,
        },
        {
          yPercent: 0,
          autoAlpha: 1,
          stagger: 0.01,
        },
        0
      );
    }

    if (this.btn) {
      tl.fromTo(
        this.btn,
        {
          yPercent: 100,
          autoAlpha: 0,
        },
        {
          yPercent: 0,
          autoAlpha: 1,
        },
        0
      );
    }

    if (this.line) {
      tl.fromTo(
        this.line,
        {
          scaleX: 0,
        },
        {
          scaleX: 1,
        },
        0
      );
    }

    if (this.listItems.length > 0) {
      ScrollTrigger.batch(this.listItems, {
        once: true,
        onEnter: batch => {
          batch.forEach((item, index) => {
            const title = item.querySelector('.capabilities__item-title span');
            const content = item.querySelector('.capabilities__wysiwyg');
            const lines = item.querySelectorAll('.line');

            const tl = gsap.timeline({
              defaults: {
                ease: t => {
                  return q1O(t);
                },
                duration: 1,
                delay: index * 0.35,
              },
            });

            gsap.set(item, { autoAlpha: 1 });

            tl.fromTo(
              title,
              {
                autoAlpha: 0,
                yPercent: 200,
              },
              {
                autoAlpha: 1,
                yPercent: 0,
                duration: 1.3,
              },
              0
            );

            tl.fromTo(
              content,
              {
                autoAlpha: 0,
                y: 50,
              },
              {
                autoAlpha: 1,
                duration: 1.3,
                y: 0,
              },
              0.35
            );

            tl.fromTo(
              lines,
              {
                scaleX: 0,
              },
              {
                scale: 1,
                stagger: 0.3,
                duration: 1.3,
              },
              0.6
            );
          });
        },
      });
    }

    if (this.arrowIcons) {
      tl.fromTo(
        this.arrowIcons,
        {
          y: 100,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1,
          duration: 1.3,
        },
        0.2
      );
    }
  }

  buildHover() {
    this.listItems.forEach(el => {
      const all = el.querySelectorAll('.capabilities__item-title__inner');
      const imageWrapper = el.querySelector('.capabilities__item-image');
      const image = imageWrapper ? imageWrapper.querySelector('img') : null;
      const first = all[0];
      const last = all[1];

      if (!image) {
        return;
      }

      const splitFirst = this.buildSplitText(first);
      const splitLast = this.buildSplitText(last);

      const stag = (40 / splitFirst.chars.length) * 0.025;
      const stag1 = (40 / splitFirst.chars.length) * 0.015;

      const tl = this.buildTitlesAnimation(
        splitFirst,
        splitLast,
        first,
        last,
        stag,
        stag1
      );

      el.addEventListener('mouseenter', () => {
        this.onMouseEnter(tl, imageWrapper, image);
      });

      el.addEventListener('mouseleave', () => {
        this.onMouseLeave(tl, imageWrapper);
      });

      el.addEventListener('mousemove', e => {
        this.onMouseMove(imageWrapper, e);
      });
    });
  }

  buildTitlesAnimation(splitFirst, splitLast, first, last, stag, stag1) {
    const tl = gsap.timeline({
      paused: true,
      defaults: {
        ease: 'none',
        duration: 1,
      },
    });

    tl.set(last, { autoAlpha: 1 });
    tl.to(
      splitFirst.chars,
      {
        yPercent: -100,
        // autoAlpha: 0,
        stagger: stag,
        rotationX: -90,
      },
      0
    );
    tl.fromTo(
      splitLast.chars,
      {
        yPercent: 100,
        // autoAlpha: 0,
        rotationX: 90,
      },
      {
        yPercent: 0,
        // autoAlpha: 1,
        rotationX: 0,
        stagger: stag1,
        color: '#005df8',
      },
      0
    );

    return tl;
  }

  onMouseEnter(tl, imageWrapper, image) {
    gsap.to(tl, {
      time: tl.duration(),
      ease: t => {
        return q1O(t);
      },
      duration: 1.3,
      overwrite: true,
    });

    gsap.to(imageWrapper, {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
      autoAlpha: 1,
      scale: 1,
      ease: t => {
        return q1O(t);
      },
      duration: 1.3,
      overwrite: true,
    });

    gsap.to(image, {
      scale: 1,
      ease: t => {
        return q1O(t);
      },
      duration: 1.3,
      // overwrite: true,
    });
    if (!this.lastMouse) {
      this.lastMouse = {
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2,
      };
    }
    const updateLastMouse = e => (this.lastMouse = e);
    window.addEventListener('mousemove', updateLastMouse, {
      passive: true,
      once: true,
    });
    this.scrollHandler = () => {
      this.onMouseMove(imageWrapper, this.lastMouse);
    };
    window.addEventListener('scroll', this.scrollHandler);
  }

  onMouseLeave(tl, image) {
    gsap.to(tl, {
      time: 0,
      duration: 0.7,
      ease: t => {
        return s0(t);
      },
      overwrite: true,
    });

    gsap.to(image, {
      clipPath: 'polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)',
      duration: 0.7,
      overwrite: true,
      ease: t => {
        return s0(t);
      },
    });
    window.removeEventListener('scroll', this.scrollHandler);
  }

  onMouseMove(image, e) {
    const rTo = gsap.quickTo(image, 'rotation', {
      duration: 0.7,
      ease: t => {
        return q1O(t);
      },
    });
    const xTo = gsap.quickTo(image, 'x', {
      duration: 0.7,
      ease: t => {
        return q1O(t);
      },
    });
    const yTo = gsap.quickTo(image, 'y', {
      duration: 0.7,
      ease: t => {
        return q1O(t);
      },
    });

    const x = e.clientX / window.innerWidth; // (0 to 1)
    const limitX =
      x < 0.5
        ? gsap.utils.mapRange(0, 0.5, -100, 0, x)
        : gsap.utils.mapRange(0.5, 1, 0, 100, x);
    const rotation =
      x < 0.5
        ? gsap.utils.mapRange(0, 0.5, -20, 0, x)
        : gsap.utils.mapRange(0.5, 1, 0, 20, x);
    xTo(limitX);
    yTo(e.clientY);
    rTo(rotation);
  }

  destroy() {}
}

export default Capabilities;



//////////////////
// WEBPACK FOOTER
// ./scripts/components/Capabilities.js
// module id = 28
// module chunks = 0